import { useMemo, useState } from 'react';
import { PRODUCT_PERMISSIONS } from '@/constants/permissions';
import { AdvancedFilterButton, FilterPanelCollapse } from '@/components/common/AdvancedFilter';
import { DataTable } from '@/components/common/DataTable';
import { DateRangeBadge } from '@/components/common/DateRangeBadge';
import { ErrorState } from '@/components/common/ErrorState';
import { ExportButton } from '@/components/common/ExportButton';
import { StatCardGrid, type LabeledStat } from '@/components/common/StatCardGrid';
import { PageHeader } from '@/components/layout/PageHeader';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { useInfiniteTableQuery } from '@/hooks/useInfiniteTableQuery';
import {
  exportSpecimenInstitution,
  specimenInstitutionInfiniteQueryOptions,
} from '../api/product.api';
import { ProductFilterPanel } from '../components/ProductFilterPanel';
import { getSpecimenInstitutionColumns } from '../components/ProductColumns';
import { TeacherNameFormModal } from '../components/TeacherNameFormModal';
import { useInstitutionFilters } from '../hooks/useInstitutionFilters';
import { useSpecimenFilterField } from '../hooks/useSpecimenFilterField';
import type { SpecimenInstitutionRow, SpecimenInstitutionTotals } from '../types/product.types';

const SEARCH_PLACEHOLDER = 'Institute, teacher, mobile or book';

type InstitutionStatCard = Omit<LabeledStat, 'value'> & { key: keyof SpecimenInstitutionTotals };

const INSTITUTION_STAT_CARDS: readonly InstitutionStatCard[] = [
  { key: 'specimenQty', label: 'Specimen Qty' },
  { key: 'institutions', label: 'Institutions' },
  { key: 'teachers', label: 'Teachers' },
  { key: 'zms', label: 'ZMs' },
];

export function SpecimenInstitutionPage() {
  const controller = useInstitutionFilters();
  const { rows, firstPageMeta, isLoading, loadError, refetch, loadMoreProps } =
    useInfiniteTableQuery(specimenInstitutionInfiniteQueryOptions(controller.params));
  const totals = firstPageMeta?.totals;

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<SpecimenInstitutionRow | null>(null);
  const specimenField = useSpecimenFilterField(controller.filterValues.specimenId ?? '');

  const { search } = controller.params;
  const columns = useMemo(
    () => getSpecimenInstitutionColumns({ onEditTeacherName: setEditingEntry, searchText: search }),
    [search],
  );

  return (
    <div className={FIT_TO_SCREEN_PAGE_CLASS}>
      <PageHeader
        title="View Specimen Institution"
        titleAddon={
          <DateRangeBadge
            onClick={() => setIsFilterOpen(true)}
            dateFrom={controller.params.dateFrom}
            dateTo={controller.params.dateTo}
          />
        }
        search={{
          value: controller.searchText,
          onChange: controller.setSearchText,
          placeholder: SEARCH_PLACEHOLDER,
        }}
        actions={
          <>
            <AdvancedFilterButton
              isOpen={isFilterOpen}
              activeCount={controller.activeFilterCount}
              onClick={() => setIsFilterOpen((wasOpen) => !wasOpen)}
            />
            <ExportButton
              requires={PRODUCT_PERMISSIONS.SPECIMEN_INSTITUTION_EXPORT}
              onExport={() => exportSpecimenInstitution(controller.params)}
              disabled={isLoading || loadError !== null}
            />
          </>
        }
      />

      <FilterPanelCollapse isOpen={isFilterOpen}>
        <ProductFilterPanel filters={controller} pageFields={[specimenField]} />
      </FilterPanelCollapse>

      {loadError ? (
        <ErrorState
          error={loadError}
          fallbackTitle="Could not load the list"
          onRetry={() => void refetch()}
          fill
        />
      ) : (
        <>
          <StatCardGrid
            isLoading={isLoading}
            stats={INSTITUTION_STAT_CARDS.map((card) => ({ ...card, value: totals?.[card.key] }))}
          />

          <DataTable
            data={rows}
            columns={columns}
            isLoading={isLoading}
            className="lg:flex-1"
            stickyColumnId="instituteName"
            emptyMessage="No institute visit in this period"
            emptyHint="Try a wider date range."
            {...loadMoreProps}
          />
        </>
      )}

      <TeacherNameFormModal
        entry={editingEntry}
        open={editingEntry !== null}
        onOpenChange={(isOpen) => {
          if (!isOpen) setEditingEntry(null);
        }}
      />
    </div>
  );
}
